import React, { useState } from 'react';
import { Add, Block, Refresh, Send, Visibility } from '@mui/icons-material';
import { cn } from '@utils/cn';
import { Button } from '@components/atoms/Button';
import { Modal } from '@components/molecules/Modal';
import { InvoiceStatusBadge } from '@components/atoms/InvoiceStatusBadge';
import { InvoicePreviewDrawer } from '@components/organisms/InvoicePreviewDrawer';
import { InvoiceManagerSlider } from '@components/organisms/InvoiceManagerSlider';
import { ConnectPaymentAccountModal } from '@components/organisms/ConnectPaymentAccountModal';
import {
  useGetInvoicesByProject,
  useRefreshInvoiceStatus,
  useResendInvoiceEmail,
  useVoidInvoice,
} from '@hooks/api/invoice';
import { useMyPaymentAccount } from '@hooks/api/merchantPaymentAccount';
import { useMyInvoicingPaused } from '@hooks/api/organizations';
import { MerchantAccountStatus } from '@models/merchantPaymentAccount.model';
import { toast } from 'react-toastify';
import {
  Invoice,
  InvoiceStatus,
  INVOICING_PAUSED_MESSAGE,
} from '@models/invoice.model';
import { UserRoles } from '@models/user.model';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { formatForDisplay } from '@utils/formatDate';

interface ProjectInvoicesTabProps {
  projectId: string;
  projectName?: string;
  defaultRecipientEmail?: string;
}

const fmtCurrency = (n: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
    Number(n || 0),
  );

/**
 * Per-project Invoices tab for the Project detail page. Renders a compact,
 * pane-friendly table of this project's invoices (view / resend / void /
 * refresh), a "New invoice" button that opens the full InvoiceManagerSlider,
 * and — when the org hasn't connected its NMI keys yet — a "Setup Invoicing"
 * entry point (owner opens the connect wizard; non-owners see who to contact).
 */
export const ProjectInvoicesTab: React.FC<ProjectInvoicesTabProps> = ({
  projectId,
  projectName,
  defaultRecipientEmail,
}) => {
  const organizationName = useAppSelector(
    (state: RootState) => state.organization.name,
  );
  const role = useAppSelector((state: RootState) => state.user.role);
  const isOwner = role === UserRoles.OWNER;

  const { data: invoices = [], isLoading } = useGetInvoicesByProject(projectId);

  // Whether the org has connected its NMI keys. `isSuccess` keeps the Setup
  // button from flashing while the account query is disabled/loading.
  const paymentAccount = useMyPaymentAccount();
  const isInvoicingSetUp =
    paymentAccount.data?.status === MerchantAccountStatus.CONNECTED;
  const showSetupButton = paymentAccount.isSuccess && !isInvoicingSetUp;

  const [showConnectModal, setShowConnectModal] = useState(false);
  const [isManagerOpen, setIsManagerOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [voidingInvoice, setVoidingInvoice] = useState<Invoice | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [resendingInvoice, setResendingInvoice] = useState<Invoice | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resendError, setResendError] = useState<string | null>(null);

  const { data: invoicingPaused } = useMyInvoicingPaused();
  const voidInvoice = useVoidInvoice();
  const resendInvoice = useResendInvoiceEmail();
  const refreshStatus = useRefreshInvoiceStatus();

  const handleView = (invoice: Invoice) => {
    setPreviewInvoice(invoice);
    if (invoice.status === InvoiceStatus.SENT) {
      refreshStatus.mutate(invoice.invoiceId, {
        onSuccess: (refreshed) => {
          if (!refreshed) return;
          setPreviewInvoice((cur) =>
            cur && cur.invoiceId === refreshed.invoiceId ? refreshed : cur,
          );
        },
      });
    }
  };

  const handleStartResend = (invoice: Invoice) => {
    // Resending reaches the payment provider, so a super-admin pause blocks it
    // server-side. Say so plainly instead of surfacing the raw server error.
    if (invoicingPaused) {
      toast.info(INVOICING_PAUSED_MESSAGE);
      return;
    }
    setResendingInvoice(invoice);
    setResendEmail(invoice.recipientEmail || '');
    setResendError(null);
  };
  const handleConfirmResend = () => {
    if (!resendingInvoice) return;
    const trimmed = resendEmail.trim();
    if (!trimmed) {
      setResendError('Please enter a recipient email.');
      return;
    }
    resendInvoice.mutate(
      { invoiceId: resendingInvoice.invoiceId, recipientEmail: trimmed },
      {
        onSuccess: () => {
          setResendingInvoice(null);
          setResendEmail('');
        },
      },
    );
  };

  const handleStartVoid = (invoice: Invoice) => {
    // Voiding closes the invoice in the payment provider, so a pause blocks it too.
    if (invoicingPaused) {
      toast.info(INVOICING_PAUSED_MESSAGE);
      return;
    }
    setVoidingInvoice(invoice);
    setVoidReason('');
  };
  const handleConfirmVoid = () => {
    if (!voidingInvoice) return;
    voidInvoice.mutate(
      { invoiceId: voidingInvoice.invoiceId, reason: voidReason.trim() || undefined },
      {
        onSuccess: () => {
          setVoidingInvoice(null);
          setVoidReason('');
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 min-w-0">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-gray-500 whitespace-nowrap">
          {invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'}
        </p>
        <div className="flex items-center gap-2">
          {showSetupButton &&
            (isOwner ? (
              <Button variant="secondary" onClick={() => setShowConnectModal(true)}>
                Setup Invoicing
              </Button>
            ) : (
              // Only the owner holds the org's payment keys, so non-owners get a
              // disabled button explaining who to reach out to.
              <span title="Only the organization owner can set up invoicing. Please contact your owner.">
                <Button variant="secondary" disabled>
                  Setup Invoicing
                </Button>
              </span>
            ))}
          <Button
            leftIcon={<Add sx={{ fontSize: 18 }} />}
            onClick={() => setIsManagerOpen(true)}>
            New invoice
          </Button>
        </div>
      </div>

      {/* Minimalistic, pane-friendly table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading && invoices.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">
            Loading invoices…
          </div>
        ) : invoices.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-600 mb-3">No invoices yet.</p>
            <Button
              variant="secondary"
              leftIcon={<Add sx={{ fontSize: 18 }} />}
              onClick={() => setIsManagerOpen(true)}>
              Create the first invoice
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  <th className="px-3 py-2.5 text-left font-medium">Invoice</th>
                  <th className="px-3 py-2.5 text-left font-medium">Recipient</th>
                  <th className="px-3 py-2.5 text-left font-medium">Status</th>
                  <th className="px-3 py-2.5 text-right font-medium">Total</th>
                  <th className="px-3 py-2.5 text-right font-medium" aria-label="Actions" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.map((invoice) => {
                  const isSent = invoice.status === InvoiceStatus.SENT;
                  const isVoidable =
                    invoice.status === InvoiceStatus.SENT ||
                    invoice.status === InvoiceStatus.PAID;
                  return (
                    <tr key={invoice.invoiceId} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 align-top">
                        <button
                          type="button"
                          onClick={() => handleView(invoice)}
                          className="font-medium text-blue-800 hover:underline">
                          {invoice.invoiceNumber || invoice.invoiceId.slice(0, 8)}
                        </button>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {invoice.createdAt
                            ? formatForDisplay(invoice.createdAt)
                            : '—'}
                        </p>
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <span className="block max-w-[180px] truncate text-gray-700">
                          {invoice.recipientEmail || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <InvoiceStatusBadge status={invoice.status} size="sm" />
                      </td>
                      <td className="px-3 py-2.5 align-top text-right font-medium text-gray-900 whitespace-nowrap">
                        {fmtCurrency(Number(invoice.total), invoice.currency)}
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            type="button"
                            title="View invoice"
                            onClick={() => handleView(invoice)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-600">
                            <Visibility sx={{ fontSize: 18 }} />
                          </button>
                          {isSent && (
                            <button
                              type="button"
                              title="Resend invoice email"
                              onClick={() => handleStartResend(invoice)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-600">
                              <Send sx={{ fontSize: 18 }} />
                            </button>
                          )}
                          {isSent && (
                            <button
                              type="button"
                              title="Refresh status from NMI"
                              onClick={() => refreshStatus.mutate(invoice.invoiceId)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-600">
                              <Refresh sx={{ fontSize: 18 }} />
                            </button>
                          )}
                          {isVoidable && (
                            <button
                              type="button"
                              title="Void invoice"
                              onClick={() => handleStartVoid(invoice)}
                              className="p-1.5 rounded hover:bg-red-50 text-red-600">
                              <Block sx={{ fontSize: 18 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Read-only preview */}
      <InvoicePreviewDrawer
        isOpen={previewInvoice !== null}
        invoice={previewInvoice}
        organizationName={organizationName || undefined}
        onClose={() => setPreviewInvoice(null)}
      />

      {/* Void modal */}
      <Modal
        isOpen={voidingInvoice !== null}
        onClose={() => setVoidingInvoice(null)}
        title={`Void invoice ${voidingInvoice?.invoiceNumber || ''}`}
        description={
          voidingInvoice
            ? `This will mark invoice ${voidingInvoice.invoiceNumber || voidingInvoice.invoiceId.slice(0, 8)} as voided and close it in NMI. The budget items charged here will be released so you can include them in a new invoice. The recipient will be notified by email. This action cannot be undone.`
            : ''
        }
        size="small"
        handleConfirm={handleConfirmVoid}
        confirmText={voidInvoice.isPending ? 'Voiding…' : 'Void invoice'}
        cancelText="Cancel"
        isLoading={voidInvoice.isPending}>
        <div className="flex flex-col gap-2">
          <label
            className="text-xs uppercase tracking-wide text-gray-500"
            htmlFor="project-invoices-void-reason">
            Reason <span className="text-gray-400 normal-case">(optional)</span>
          </label>
          <textarea
            id="project-invoices-void-reason"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="e.g., Created in error, client cancelled order…"
            rows={3}
            className="px-3 py-2 border border-gray-300 rounded text-sm bg-white resize-none"
          />
        </div>
      </Modal>

      {/* Resend modal */}
      <Modal
        isOpen={resendingInvoice !== null}
        onClose={() => setResendingInvoice(null)}
        title={`Resend invoice ${resendingInvoice?.invoiceNumber || ''}`}
        description="The recipient will receive the invoice email again at the address below."
        size="small"
        handleConfirm={handleConfirmResend}
        confirmText={resendInvoice.isPending ? 'Sending…' : 'Resend'}
        cancelText="Cancel"
        isLoading={resendInvoice.isPending}>
        <div className="flex flex-col gap-2">
          <label
            className="text-xs uppercase tracking-wide text-gray-500"
            htmlFor="project-invoices-resend-email">
            Recipient email
          </label>
          <input
            id="project-invoices-resend-email"
            type="email"
            value={resendEmail}
            onChange={(e) => {
              setResendEmail(e.target.value);
              if (resendError) setResendError(null);
            }}
            placeholder="invoices@client.com"
            className={cn(
              'px-3 py-2 border rounded text-sm bg-white',
              resendError ? 'border-red-400' : 'border-gray-300',
            )}
          />
          {resendError && <p className="text-xs text-red-600">{resendError}</p>}
        </div>
      </Modal>

      {/* Owner-only invoicing setup (NMI key connection) */}
      <ConnectPaymentAccountModal
        open={showConnectModal}
        onClose={() => setShowConnectModal(false)}
      />

      {/* Full create / send experience */}
      {isManagerOpen && (
        <InvoiceManagerSlider
          isOpen={isManagerOpen}
          projectId={projectId}
          projectName={projectName}
          defaultRecipientEmail={defaultRecipientEmail}
          onClose={() => setIsManagerOpen(false)}
        />
      )}
    </div>
  );
};

export default ProjectInvoicesTab;
